import {
  closestCenter,
  defaultDropAnimation,
  DndContext,
  DragOverlay,
  DropAnimation,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { keyBy } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from 'react-query';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ProjectId } from 'api/models';
import { TaskCard } from 'components/organisms/projects/project-plan/swimlane/task-card';
import { Task } from 'data/tasks';
import { useUpdateTaskOrderOfPhaseMutation } from 'queries';
import { getProjectDetailQueryKey } from 'queries/common';

const Wrapper = styled.div<{ isDragOverlay?: boolean }>`
  padding: var(--spacing-03) 0;
  ${ifProp(
    'isDragOverlay',
    css`
      z-index: 999;
    `,
  )};
`;

interface SortableItemProps {
  disabled?: boolean;
  id: string;
  item: Task;
  taskFilterFunction: (task: Task) => boolean;
}

export const SortableItem = ({
  disabled,
  id,
  item,
  taskFilterFunction,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Wrapper ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard task={item} filterFunction={taskFilterFunction} />
    </Wrapper>
  );
};

export interface Props {
  projectId: number;
  phaseId: number;
  dropAnimation?: DropAnimation | null;
  items: Task[];
  isDisabled?: boolean;
  itemsOrder?: number[];
  taskFilterFunction: (task: Task) => boolean;
}

const defaultDropAnimationConfig: DropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

export const Sortable: FC<Props> = ({
  projectId,
  phaseId,
  items: initialItems = [],
  isDisabled = false,
  itemsOrder = [],
  taskFilterFunction: taskFilter,
}) => {
  const [orderedItems, setOrderedItems] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { mutate } = useUpdateTaskOrderOfPhaseMutation(phaseId);

  const initialItemsMap = useMemo(() => {
    return keyBy(initialItems, 'taskId');
  }, [initialItems]);

  useDeepCompareEffect(() => {
    setOrderedItems(itemsOrder.map(String));
  }, [itemsOrder]);

  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const getIndex = (_id: string) => orderedItems.indexOf(_id);
  const activeIndex = activeId ? getIndex(activeId) : -1;

  return (
    <DndContext
      id={phaseId.toString()}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[
        restrictToVerticalAxis,
        restrictToWindowEdges,
        restrictToFirstScrollableAncestor,
      ]}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }

        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);

          if (activeIndex !== overIndex) {
            const result = arrayMove<string>(
              orderedItems,
              activeIndex,
              overIndex,
            );

            mutate(
              { taskOrder: result.map((each) => Number(each)) },
              {
                onSuccess: () =>
                  queryClient.invalidateQueries(
                    getProjectDetailQueryKey(ProjectId(projectId)),
                  ),
              },
            );
            setOrderedItems(result);
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={orderedItems} strategy={rectSortingStrategy}>
        <div>
          {orderedItems.map((idx) => {
            const item = initialItemsMap[idx];

            return item ? (
              <SortableItem
                key={idx}
                id={idx}
                item={item}
                disabled={isDisabled}
                taskFilterFunction={taskFilter}
              />
            ) : null;
          })}
        </div>
      </SortableContext>
      {createPortal(
        <DragOverlay
          adjustScale={false}
          dropAnimation={defaultDropAnimationConfig}
        >
          {activeId && initialItemsMap[activeId] ? (
            <Wrapper isDragOverlay>
              <TaskCard
                task={initialItemsMap[activeId]}
                filterFunction={taskFilter}
              />
            </Wrapper>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
